import React from "react";
import { useDispatch } from "react-redux";

import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

import Button from "components/common/Button";
import AddPaymentLoaderModal from "components/Screens/CustomerOnboarding/AddPaymentLoaderModal";

import { AlertType } from "components/common/Snackbar";
import { showSnackbar } from "store/actions/Utility";
import { customerOnboardingAddPayment } from "services/customer";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { CUSTOMER_ONBOARDING_PAYMENT_SUCCESS_ROUTE } from "constants/routes";

import "./AddPaymentMethod.scss";

function AddCreditCard({
  handleBackToPaymentMethods,
  loading,
  setLoading,
  navigate,
  customerId,
  isStepSkippable,
  onSkip,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const handleCreditCardSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || loading) {
      return;
    }

    setLoading(true);

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + CUSTOMER_ONBOARDING_PAYMENT_SUCCESS_ROUTE,
        },
        redirect: "if_required",
      });

      if (error) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: (
              <div>
                Your card details could not be processed. <strong>Reason:</strong> {error.message}
              </div>
            ),
          })
        );

        setLoading(false);
      } else if (setupIntent && setupIntent.status === "succeeded") {
        const { id, payment_method: paymentMethodId } = setupIntent;
        const payload = { paymentMethodType: "CREDIT_CARD", setupIntentId: id, paymentMethodId };
        const response = await customerOnboardingAddPayment(payload, customerId);
        // Leave loading on until the redirect navigates away so the form cannot be resubmitted
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.success,
            message: "Payment method added successfully.",
          })
        );

        const { chargeInitiated = true, scheduledAt = null } = response ?? {};
        setTimeout(() => {
          navigate(CUSTOMER_ONBOARDING_PAYMENT_SUCCESS_ROUTE, {
            state: {
              chargeInitiated,
              scheduledAt,
            },
          });
        }, 2000);
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: error.message || "Failed to save payment method",
        })
      );

      setLoading(false);
    }
  };

  return (
    <div className="onboarding-add-credit-card-payment-container">
      <div className="onboarding-add-credit-card-payment-main-div">
        <div className="credit-card-header">
          <ArrowBackIcon className="back-button-icon" onClick={handleBackToPaymentMethods} />
          <div>
            <div className="heading-h3-semibold color-text-heading-primary">Enter your card details</div>
          </div>
        </div>

        <div className="mb-1 banner-bg">
          <p className="para-body-m-medium color-text-body-primary">
            Card payments include a surcharge. Pay with{" "}
            <span
              className="cursor-pointer color-text-action"
              onClick={handleBackToPaymentMethods}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") handleBackToPaymentMethods();
              }}
            >
              ACH Direct Debit
            </span>{" "}
            to avoid this fee.
          </p>
        </div>
        <form onSubmit={handleCreditCardSubmit} className="credit-card-form">
          <div className="payment-element-container">
            <PaymentElement
              options={{
                layout: {
                  type: "auto",
                },
                fields: {
                  billingDetails: {
                    name: "auto",
                    email: "auto",
                    phone: "auto",
                    address: {
                      country: "auto",
                      line1: "auto",
                      line2: "auto",
                      city: "auto",
                      state: "auto",
                      postalCode: "auto",
                    },
                  },
                },
              }}
            />
          </div>
        </form>
      </div>

      <div className="onboarding-bottom-button">
        <div className="onboarding-bottom-button-container">
          {isStepSkippable ? (
            <Button onClick={onSkip} name="Skip" size="large" label="Skip" isFullWidth={false} />
          ) : (
            <Button
              onClick={handleCreditCardSubmit}
              name="Add payment method"
              size="large"
              label="Add payment method"
              isFullWidth={false}
              isDisabled={!stripe || !elements}
              isLoading={loading}
            />
          )}
        </div>
      </div>
      {loading && (
        <AddPaymentLoaderModal
          title="Hang tight, We’re processing your payment..."
          description="Just a moment while we securely save your payment details. This won’t take long."
          open={loading}
        />
      )}
    </div>
  );
}

export default AddCreditCard;
